// Use global logger to avoid circular dependencies
const log = typeof window !== 'undefined' && window.Log ? window.Log('signals.js') : {
  blue: () => {}, red: () => {}, green: () => {}, yellow: () => {}, purple: () => {}, log: () => {}, table: () => {}
};

let subscriber = null;

export function signal(initialValue) {
    let value = initialValue;
    const subscriptions = new Set();

    return {
        get value() {
            if (subscriber) {
                subscriptions.add(subscriber);
                // Track which signals this subscription is subscribed to (for cleanup)
                if (subscriber.subscribedSignals) {
                    subscriber.subscribedSignals.add(this);
                }
                log.blue('Added subscriber to signal, total subscribers:', subscriptions.size);
            }
            return value;
        },
        set value(updated) {
            value = updated;
            // Only notify active subscriptions
            subscriptions.forEach((subscriber) => {
                if (subscriber.active) {
                    try {
                        subscriber.fn();
                    } catch (error) {
                        log.red('Error in signal subscription:', error);
                        // Remove faulty subscription
                        subscriptions.delete(subscriber);
                    }
                }
            });
        },
        // Internal method to remove subscriptions (for cleanup)
        _removeSubscription(subscriber) {
            subscriptions.delete(subscriber);
            log.blue('Removed subscriber from signal, remaining:', subscriptions.size);
        },
        // Get subscription count for debugging
        get _subscriptionCount() {
            return subscriptions.size;
        }
    }
}

export function effect(fn) {
    // Create a subscription object to track this effect
    const subscriptionObject = {
        fn,
        active: true,
        cleanup: null,
        subscribedSignals: new Set()
    };
    
    // Store the cleanup function that will be returned
    const cleanup = () => {
        if (!subscriptionObject.active) {
            log.yellow('Effect already cleaned up');
            return;
        }
        
        subscriptionObject.active = false;
        
        // Remove this subscription from all signals it was subscribed to
        subscriptionObject.subscribedSignals.forEach((signal) => {
            if (signal._removeSubscription) {
                signal._removeSubscription(subscriptionObject);
            }
        });
        
        // Clear the signal references
        subscriptionObject.subscribedSignals.clear();
        
        log.green('Effect cleaned up successfully');
    };
    
    subscriptionObject.cleanup = cleanup;
    
    // Temporarily store the subscription object as the current subscriber
    subscriber = subscriptionObject;
    
    // Execute the effect function immediately
    // This initial execution will track any signal dependencies
    try {
        fn();
    } catch (error) {
        log.red('Error in effect execution:', error);
        cleanup(); // Clean up on error
        throw error;
    }
    
    // Clear the subscriber after the effect runs
    subscriber = null;
    
    // Return the cleanup function so components can clean up when needed
    return cleanup;
}

/**
 * Creates a derived signal that automatically updates when its dependencies change.
 * 
 * A derived signal is a computed value that depends on other signals. When any of the
 * source signals change, the derived signal updates automatically.
 * 
 * @param {Function} fn - A function that computes the derived value. Any signals accessed
 *                        within this function will become dependencies of the derived signal.
 * @returns {Object} A read-only signal containing the computed value.
 * 
 * @example
 * const count = signal(0);
 * const doubled = derived(() => count.value * 2);
 * console.log(doubled.value); // 0
 * count.value = 5;
 * console.log(doubled.value); // 10 (automatically updated)
 */
export function derived(fn) {
    // Create a new signal to hold the derived value
    const derivedSignal = signal();
    
    // Set up an automatic effect that:
    // 1. Runs the computation function
    // 2. Updates the derived signal with the result
    // 3. Automatically re-runs when dependencies change
    const cleanup = effect(() => {
        // When any signal used in fn() changes, this effect will re-run
        // and update the derived value accordingly
        derivedSignal.value = fn();
    });
    
    // Add cleanup method to the derived signal for proper resource management
    derivedSignal._cleanup = cleanup;
    
    // Return the derived signal which can be accessed like any other signal
    return derivedSignal;
}

/**
 * Creates a managed effect that tracks cleanup functions for batch cleanup.
 * This is useful for components that need to clean up multiple effects at once.
 * 
 * @param {Function} fn - The effect function to run
 * @param {Array} cleanupArray - Array to store cleanup functions for batch cleanup
 * @returns {Function} Cleanup function for this specific effect
 * 
 * @example
 * class MyComponent extends HTMLElement {
 *   constructor() {
 *     super();
 *     this._cleanupFunctions = [];
 *   }
 * 
 *   connectedCallback() {
 *     managedEffect(() => {
 *       // Your reactive code here
 *     }, this._cleanupFunctions);
 *   }
 * 
 *   disconnectedCallback() {
 *     this._cleanupFunctions.forEach(cleanup => cleanup());
 *     this._cleanupFunctions = [];
 *   }
 * }
 */
export function managedEffect(fn, cleanupArray) {
    const cleanup = effect(fn);
    
    if (cleanupArray && Array.isArray(cleanupArray)) {
        cleanupArray.push(cleanup);
        log.blue('Added effect to managed cleanup array, total managed effects:', cleanupArray.length);
    }
    
    return cleanup;
}

/**
 * Utility function to clean up all effects in an array and clear the array.
 * 
 * @param {Array} cleanupArray - Array of cleanup functions
 */
export function cleanupEffects(cleanupArray) {
    if (!Array.isArray(cleanupArray)) {
        log.yellow('cleanupEffects called with non-array argument');
        return;
    }
    
    const count = cleanupArray.length;
    cleanupArray.forEach(cleanup => {
        if (typeof cleanup === 'function') {
            cleanup();
        }
    });
    cleanupArray.length = 0; // Clear the array
    
    log.green('Cleaned up', count, 'effects');
}
